/**
 * Per-user saved starred lists (compare-view shortlists).
 *
 * Backed by the server (`/api/saved-lists`, scoped to the logged-in user) so a
 * user can save the drugs they starred for a search and find that shortlist
 * later — across devices.
 */
import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import type { SavedStarredList, SavedListDrug, SearchDescriptor } from '../types';

const QUERY_KEY = ['saved-lists'];

export const SAVED_LISTS_LIMIT = 50;

export function useSavedLists(limit = SAVED_LISTS_LIMIT) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<SavedStarredList[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => (await authApi.listSavedLists(limit)).lists,
    staleTime: 30 * 1000,
    retry: false,
  });

  const createMut = useMutation({
    mutationFn: (input: { name: string; drugs: SavedListDrug[]; search_context?: SearchDescriptor | null }) =>
      authApi.createSavedList(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, ...input }: { id: number; drugs: SavedListDrug[]; name?: string | null; search_context?: SearchDescriptor | null }) =>
      authApi.updateSavedList(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => authApi.renameSavedList(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => authApi.deleteSavedList(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const saveList = useCallback(
    (input: { name: string; drugs: SavedListDrug[]; search_context?: SearchDescriptor | null }) =>
      createMut.mutateAsync(input),
    [createMut],
  );
  const updateList = useCallback(
    (id: number, input: { drugs: SavedListDrug[]; name?: string | null; search_context?: SearchDescriptor | null }) =>
      updateMut.mutateAsync({ id, ...input }),
    [updateMut],
  );
  const renameList = useCallback((id: number, name: string) => renameMut.mutate({ id, name }), [renameMut]);
  const deleteList = useCallback((id: number) => deleteMut.mutate(id), [deleteMut]);

  return {
    lists: data ?? [],
    isLoading,
    saving: createMut.isPending || updateMut.isPending,
    saveList,
    updateList,
    renameList,
    deleteList,
  };
}
